import React, { useState, useEffect } from 'react';
import styles from './ProductDetails.module.css';
import DOMPurify from 'dompurify';
import { supabase } from '../../lib/supabase';
import { useStore } from '../../store/StoreContext';
import { useDispatch } from 'react-redux';
import { addToCart } from '../../store/cartSlice';
import toast from 'react-hot-toast';
import { useStoreSettings } from '../../hooks/useStoreSettings';

interface ProductDetailsProps {
  name: string;
  description: string;
  short_description: string;
  price: number;
  old_price?: number;
  id: string;
}

interface Bundle {
  id: string;
  name: string;
  description: string;
  bundle_type: string;
  discount_type: string;
  discount_value: number;
  box_width?: string;
  box_height?: string;
}

interface BundleProductItem {
  bundle_id: string;
  product_id: string;
  quantity: number;
  is_gift: boolean;
  bundle: Bundle;
}

interface BundleWithProducts {
  bundle: Bundle;
  products: {
    product: Product;
    quantity: number;
    is_gift: boolean;
  }[];
}

interface Product {
  id: string;
  name: string;
  price: number;
  image_url: string;
}

interface BundleProduct {
  bundle_id: string;
  quantity: number;
  is_gift: boolean;
  bundle: {
    id: string;
    name: string;
    description: string;
    bundle_type: string;
    discount_type: string;
    discount_value: number;
    box_width?: string;
    box_height?: string;
  };
}

interface BundleProductItemFromDB {
  quantity: number;
  is_gift: boolean;
  product: {
    id: string;
    name: string;
    price: number;
    image_url: string;
  };
}

interface RawBundleProductItem {
  quantity: number;
  is_gift: boolean;
  product: {
    id: string;
    name: string;
    price: number;
    image_url: string;
  };
}

const ProductDetails: React.FC<ProductDetailsProps> = ({
  id
}) => {
  const [bundleItems, setBundleItems] = useState<BundleWithProducts[]>([]);
  const { currency } = useStore();
  const dispatch = useDispatch();
  const { defaultCurrency } = useStoreSettings();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('sr-RS', {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  // Discount math is driven by discount_type/discount_value — bundle_type is
  // a UI hint (the field label in admin is "Vrednost Popusta"). Mirrors the
  // bundle-module logic in ProductPage.fetchBundleProducts.
  //  - PERCENTAGE: discount_value is a percent off each item.
  //  - FIXED_AMOUNT: discount_value is a flat RSD discount on the whole
  //    bundle, spread proportionally by each item's share of the total.
  const itemDiscountedPrice = (
    itemOriginal: number,
    isGift: boolean,
    bundle: Bundle,
    totalOriginal: number,
  ): number => {
    if (isGift) return 0;
    if (bundle.discount_type === 'PERCENTAGE' && bundle.discount_value > 0) {
      return itemOriginal * (1 - bundle.discount_value / 100);
    }
    if (bundle.discount_type === 'FIXED_AMOUNT' && bundle.discount_value > 0) {
      if (totalOriginal <= 0) return itemOriginal;
      const share = bundle.discount_value * (itemOriginal / totalOriginal);
      return Math.max(0, itemOriginal - share);
    }
    return itemOriginal;
  };

  const bundleOriginalTotal = (bundle: BundleWithProducts) =>
    bundle.products.reduce((sum, item) => sum + (item.is_gift ? 0 : item.product.price), 0);

  const fetchBundleItems = async () => {
    try {
      console.log('Fetching bundle items for product:', id);

      // Prvo dohvatamo bundle ID-eve koji sadrže ovaj proizvod
      const { data: bundleIds, error: bundleError } = await supabase
        .from('bundle_product_items')
        .select('bundle_id')
        .eq('product_id', id);

      if (bundleError) throw bundleError;
      console.log('Found bundle IDs:', bundleIds);

      if (bundleIds && bundleIds.length > 0) {
        // Zatim dohvatamo sve detalje o bundle-ovima
        const { data: bundles, error: bundlesError } = await supabase
          .from('bundles')
          .select('*')
          .in('id', bundleIds.map(b => b.bundle_id));

        if (bundlesError) throw bundlesError;
        console.log('Found bundles:', bundles);

        // Za svaki bundle dohvatamo njegove proizvode
        const bundlesWithProducts = await Promise.all(
          bundles.map(async (bundle) => {
            const { data, error: productsError } = await supabase
              .from('bundle_product_items')
              .select(`
                quantity,
                is_gift,
                sort_order,
                product:products (
                  id,
                  name,
                  price,
                  image_url
                )
              `)
              .eq('bundle_id', bundle.id)
              .order('sort_order', { ascending: true });

            if (productsError) throw productsError;
            
            // Dodajemo log da vidimo tačnu strukturu podataka
            console.log('Raw data from database:', JSON.stringify(data, null, 2));
            
            // Prvo pretvorimo u unknown, pa onda u naš tip
            const products = (data as unknown) as RawBundleProductItem[];

            return {
              bundle: {
                id: bundle.id,
                name: bundle.name,
                description: bundle.description,
                bundle_type: bundle.bundle_type,
                discount_type: bundle.discount_type,
                discount_value: bundle.discount_value,
                box_width: bundle.box_width,
                box_height: bundle.box_height
              },
              products: products.map(item => ({
                product: {
                  id: item.product.id,
                  name: item.product.name,
                  price: Number(item.product.price),
                  image_url: item.product.image_url
                },
                quantity: Number(item.quantity),
                is_gift: Boolean(item.is_gift)
              }))
            };
          })
        );

        console.log('Final bundles with products:', bundlesWithProducts);
        setBundleItems(bundlesWithProducts);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    if (id) {
      console.log('ProductDetails mounted with id:', id);
      fetchBundleItems();
    }
  }, [id]);

  // Dodajemo log pre renderovanja
  console.log('Current bundleItems:', bundleItems);

  if (bundleItems.length === 0) return null;

  const handleAddToCart = (bundle: BundleWithProducts) => {
    const totalOriginal = bundleOriginalTotal(bundle);
    // Dodajemo svaki proizvod iz bundle-a pojedinačno u korpu
    bundle.products.forEach(item => {
      const originalPrice = item.product.price;
      const discountedPrice = itemDiscountedPrice(
        originalPrice,
        item.is_gift,
        bundle.bundle,
        totalOriginal,
      );
      
      // Ako imamo rate faktor u valuti, moramo osigurati da cena bude tačna
      // rate se koristi pri formatiranju cene, pa delimo sa njim da dobijemo osnovnu vrednost
      let rateAdjustedPrice = discountedPrice;
      if (defaultCurrency?.rate && defaultCurrency.rate !== 1) {
        rateAdjustedPrice = discountedPrice / defaultCurrency.rate;
        console.log(`Currency rate: ${defaultCurrency.rate}, Original price: ${discountedPrice}, Rate adjusted price: ${rateAdjustedPrice}`);
      }
      
      console.log(`Adding product to cart: ${item.product.name}`);
      console.log(`Original price: ${originalPrice}, Discount: ${bundle.bundle.discount_value}%, Final price: ${discountedPrice}`);
      
      dispatch(addToCart({
        id: item.product.id,
        name: item.product.name,
        price: rateAdjustedPrice,  // Koristimo cenu prilagođenu faktoru konverzije
        image_url: item.product.image_url,
        quantity: item.quantity,
        fromBundle: true // Označavamo da je proizvod iz bundle-a
      }));
    });
    
    toast.success('Bundle je dodat u korpu!');
  };

  return (
    <div className={styles.bundlesSection}>
      {bundleItems.map((bundle) => {
        const totalOriginal = bundleOriginalTotal(bundle);
        const totalDiscounted = bundle.products.reduce(
          (sum, item) =>
            sum + itemDiscountedPrice(item.product.price, item.is_gift, bundle.bundle, totalOriginal),
          0,
        );
        const savings = Math.max(0, totalOriginal - totalDiscounted);
        return (
          <div key={bundle.bundle.id} className={styles.bundleItem}>
            <h2>{bundle.bundle.name}</h2>
            <div className={styles.bundleRow}>
              <div className={styles.bundleProducts}>
                {bundle.products.map((item, index) => (
                  <React.Fragment key={item.product.id}>
                    <div className={styles.productCard}>
                      <img
                        src={item.product.image_url}
                        alt={item.product.name}
                      />
                      <div className={styles.productInfo}>
                        <h5>{item.product.name}</h5>
                        <div className={styles.priceInfo}>
                          <span className={styles.originalPrice}>
                            {item.is_gift ? formatPrice(0) : formatPrice(item.product.price)}
                          </span>
                          <span className={styles.bundlePrice}>
                            {formatPrice(
                              itemDiscountedPrice(item.product.price, item.is_gift, bundle.bundle, totalOriginal),
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    {index < bundle.products.length - 1 && (
                      <div className={styles.plusSign}>+</div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className={styles.plusSign}>=</div>
              <div className={styles.bundleSummary}>
                <div className={styles.totalPrice}>
                  <span className={styles.originalTotal}>{formatPrice(totalOriginal)}</span>
                  <span className={styles.bundleTotal}>{formatPrice(totalDiscounted)}</span>
                </div>
                <span className={styles.savings}>Ušteda {formatPrice(savings)}!</span>
                <button className={styles.addBundleButton} onClick={() => handleAddToCart(bundle)}>
                  Dodaj komplet u korpu
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProductDetails; 